import { ArrowRightIcon, CheckCircledIcon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect } from "react";
import logo from "../assets/logo.png";
import { signalCheckoutResult } from "../utils/checkoutSignal";

export default function BillingResultView({ success }: { success: boolean }) {
    const Icon = success ? CheckCircledIcon : ExclamationTriangleIcon;
    const dashboardUrl = import.meta.env.VITE_MCP_URL || "/";

    // Tells the tab that opened Checkout (still showing "Subscription in
    // progress") what actually happened, so it doesn't sit there indefinitely
    // — including on rejection, which otherwise looks identical to a still-
    // pending success from that tab's point of view.
    useEffect(() => {
        signalCheckoutResult(success ? "success" : "cancelled");

        // Checkout opens in a separate browser tab. Once the originating
        // dashboard has been notified, close the result tab so repeated
        // checkout attempts do not leave stale tabs behind. Browsers may
        // refuse window.close() for tabs they did not consider script-opened;
        // in that case the dashboard link below remains available.
        const closeTimer = window.setTimeout(() => window.close(), 500);

        return () => window.clearTimeout(closeTimer);
    }, [
        success,
    ]);

    return (
        <main className="billing-result-page">
            <Card className="billing-result-card" data-status={success ? "success" : "error"}>
                <div className="billing-result-rail" aria-hidden="true" />
                <Flex className="billing-result-content" direction="column" align="center">
                    <Flex className="billing-result-brand" align="center" gap="2">
                        <img src={logo} alt="" aria-hidden="true" />
                        <Text size="1" weight="bold">
                            DESKTOP COMMANDER
                        </Text>
                    </Flex>

                    <div className="billing-result-icon" aria-hidden="true">
                        <Icon width="28" height="28" />
                    </div>

                    <section role={success ? "status" : "alert"} aria-labelledby="billing-result-title">
                        <Heading id="billing-result-title" size="7" align="center">
                            {success ? "Checkout complete" : "Something went wrong"}
                        </Heading>
                        <Text className="billing-result-message" as="p" size="3" color="gray" align="center">
                            {success
                                ? "Your subscription was received. Pro access will be enabled after payment confirmation."
                                : "We couldn’t complete your request. Return to the dashboard and try again."}
                        </Text>
                    </section>

                    <Button className="billing-result-action" size="3" asChild>
                        <a href={dashboardUrl}>
                            {success ? "Go to dashboard" : "Back to dashboard"}
                            <ArrowRightIcon aria-hidden="true" />
                        </a>
                    </Button>

                    <Text className="billing-result-note" as="p" size="1" color="gray" align="center">
                        This tab will close automatically. If it stays open, you can return to the dashboard above.
                    </Text>
                </Flex>
            </Card>
        </main>
    );
}
